import type { Action, BoundActions } from 'react-sweet-state';
import {
	createActionsHook,
	createHook,
	createStore,
	createContainer,
	createStateHook,
} from 'react-sweet-state';
import { useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { AiIssueCreateState } from '@atlassian/ai-issue-create';
import type { AvailableSite } from '@atlassian/gic-anywhere';

import { expVal } from '@confluence/feature-experiments';

import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload';
import type {
	CreateIssueBodyIssueLinkType,
	CreateIssueResponse,
	FieldMetadata,
	JiraProject,
	SupportedValueType,
} from '../__types__/apiUtils';
import type { InsertStorageColumnTableRequestPayload } from '../__types__/InsertStorageColumnTableRequestPayload';
import { transformStargateUrl } from '../utils/configureUtils';
import { messages } from '../messages';

import {
	useAiBulkNavigationGotoPage,
	type AiBulkNavigationPageType,
} from './AiBulkNavigationContextProvider';

export const AiStates = {
	ABORT: 'abort',
	LOADING: 'loading',
	ERROR: 'error',
	IDLE: 'idle',
} as const;

export type ManualCreateStates = 'loading' | 'error' | 'idle';

export const ErrorStates = {
	BULK_CREATION: 'bulk-issue-creation-failed',
	ISSUE_GENERATION: 'ai-issue-generation-failed',
	SITE_RETRIEVAL: 'jira-site-retreival-failed',
	PROJECT_RETRIEVAL: 'jira-project-retreival-failed',
	ISSUE_TYPE_RETRIEVAL: 'issue-type-retreival-failed',
	EMPTY: '',
} as const;

export type ErrorState = (typeof ErrorStates)[keyof typeof ErrorStates];
type NonEmptyErrorState = Exclude<ErrorState, typeof ErrorStates.EMPTY>;
export const ErrorMessageMap = ((): Record<
	NonEmptyErrorState,
	() => {
		id: string;
		defaultMessage: string;
		description: string;
	}
> => {
	return {
		[ErrorStates.BULK_CREATION]: () =>
			expVal('confluence_work_item_create_ai_rebrand_to_rovo', 'isEnabled', false)
				? messages.bulkCreateAiErrorDescriptionRebrandToRovo
				: messages.bulkCreateAiErrorDescription,
		[ErrorStates.ISSUE_GENERATION]: () =>
			expVal('confluence_work_item_create_ai_rebrand_to_rovo', 'isEnabled', false)
				? messages.issueGenerationErrorDescriptionRebrandToRovo
				: messages.issueGenerationErrorDescription,
		[ErrorStates.SITE_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
		[ErrorStates.PROJECT_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
		[ErrorStates.ISSUE_TYPE_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
	};
})();
export interface IssueType {
	iconUrl: string;
	name: string;
	id: string;
	description?: string;
	self?: string;
	subtask?: boolean;
}

export interface IssueObject {
	id: string;
	summary: string;
	description?: DocNode;
	issueType: IssueType;
	parentIssueLink?: string;
	assigneeUserId?: string;
	labels?: string[];
	assignee?: string;
	parent?: CreateIssueBodyIssueLinkType;
	// Versions must be flattened down to just an array of IDs, but we need to know the field name as well.
	additionalFields?: AdditionalIssueField[];
	rowIndex?: number;
}

export interface AdditionalIssueField {
	field: FieldMetadata;
	value: SupportedValueType;
}

type IssueTypeFields = {
	requiredFields: FieldMetadata[];
	optionalFields: FieldMetadata[];
};

// Still not fully known what the exact object shape that will be sent to us is
export type BulkIssuesListType = IssueObject[];

export type BulkCreateContextType = {
	bulkIssuesList: BulkIssuesListType;
	contentLastModifiedTimestamp?: string;
	insertStorageFragmentRequestPayload?: InsertStorageFragmentRequestPayload;
	insertStorageColumnTableRequestPayload?: InsertStorageColumnTableRequestPayload;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	isCreatedFromTable: boolean;
	isCreatedFromActionItems: boolean;
	currentJiraSite: AvailableSite;
	currentJiraProject: JiraProject;
	currentIssueId: string;
	issueTypeHasRequiredFields: boolean;
	singleIssuesCreated: CreateIssueResponse[];
	error: ErrorState;
	issueTypeFields: IssueTypeFields;
	manualCreateState: ManualCreateStates;
	aiResultViewedFiredOnce: boolean;
	expectedIssueIndices: number[];
	selectedTableAdf?: ADFEntity;
};

type State = {
	bulkIssuesList: BulkIssuesListType;
	currentIssueId: string;
	contentLastModifiedTimestamp: string | null | undefined;
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
	insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload | undefined;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	isCreatedFromTable: boolean;
	isCreatedFromActionItems: boolean;
	currentJiraSite: AvailableSite | undefined;
	currentJiraProject: JiraProject | undefined;
	currentDefaultIssueType: IssueType | undefined;
	issueTypeHasRequiredFields: boolean;
	singleIssuesCreated: CreateIssueResponse[];
	error: ErrorState;
	issueTypeFields: IssueTypeFields;
	manualCreateState: ManualCreateStates;
	aiResultViewedFiredOnce: boolean;
	closeAndAddIssuesToPage: boolean;
	expectedIssueIndices: number[];
	selectedTableAdf: ADFEntity | undefined; // The table ADF that was selected for bulk create; will only be defined when selected in the editor
};

export type IssueCreateSidePanelActions = BoundActions<State, typeof bulkCreateActions>;

// TODO: replace with initialPersistedBulkCreateState and initialTransientBulkCreateState when removing expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
const initialBulkCreateState: State = {
	bulkIssuesList: [],
	currentIssueId: '',
	contentLastModifiedTimestamp: undefined,
	insertStorageFragmentRequestPayload: undefined,
	insertStorageColumnTableRequestPayload: undefined,
	aiState: AiStates.IDLE,
	isCreatedWithAi: false,
	isCreatedFromTable: false,
	isCreatedFromActionItems: false,
	currentJiraSite: undefined,
	currentJiraProject: undefined,
	currentDefaultIssueType: undefined,
	issueTypeHasRequiredFields: false,
	singleIssuesCreated: [],
	error: '',
	issueTypeFields: {
		requiredFields: [],
		optionalFields: [],
	},
	manualCreateState: 'idle',
	aiResultViewedFiredOnce: false,
	closeAndAddIssuesToPage: false,
	expectedIssueIndices: [],
	selectedTableAdf: undefined,
};

// TODO: Uncomment when removing expVal('confluence-bulk-work-create-editor')
// // Persisted state: survives panel close/reopen to maintain user's Jira configuration
// // and track issues that need to be inserted into the page
// const initialPersistedBulkCreateState = {
// 	currentJiraSite: undefined as State['currentJiraSite'],
// 	currentJiraProject: undefined as State['currentJiraProject'],
// 	currentDefaultIssueType: undefined as State['currentDefaultIssueType'],
// 	issueTypeFields: {
// 		requiredFields: [],
// 		optionalFields: [],
// 	} as State['issueTypeFields'],
// 	issueTypeHasRequiredFields: false,
// 	singleIssuesCreated: [] as State['singleIssuesCreated'],
// };

// Transient state: reset on panel close
// Workflow-specific state for the current session
const initialTransientBulkCreateState = {
	bulkIssuesList: [] as State['bulkIssuesList'],
	currentIssueId: '',
	contentLastModifiedTimestamp: undefined as State['contentLastModifiedTimestamp'],
	insertStorageFragmentRequestPayload: undefined as State['insertStorageFragmentRequestPayload'],
	insertStorageColumnTableRequestPayload:
		undefined as State['insertStorageColumnTableRequestPayload'],
	aiState: AiStates.IDLE as State['aiState'],
	isCreatedWithAi: false,
	isCreatedFromTable: false,
	isCreatedFromActionItems: false,
	error: '' as ErrorState,
	manualCreateState: 'idle' as ManualCreateStates,
	aiResultViewedFiredOnce: false,
	closeAndAddIssuesToPage: false,
	expectedIssueIndices: [] as State['expectedIssueIndices'],
	selectedTableAdf: undefined as State['selectedTableAdf'],
};

const bulkCreateActions = {
	setCurrentIssueId:
		(currentIssueId: State['currentIssueId']): Action<State> =>
		({ setState }) => {
			setState({ currentIssueId });
		},
	setBulkIssuesList:
		(bulkIssuesList: State['bulkIssuesList']): Action<State> =>
		({ setState }) => {
			setState({ bulkIssuesList });
		},
	setIssueTypeFields:
		(issueTypeFields: State['issueTypeFields']): Action<State> =>
		({ setState }) => {
			setState({ issueTypeFields });
		},
	setAiState:
		(aiState: State['aiState']): Action<State> =>
		({ setState, getState }) => {
			const { error } = getState();
			if (error === ErrorStates.ISSUE_GENERATION && aiState == AiStates.LOADING) {
				setState({ aiState, error: ErrorStates.EMPTY });
			} else if (aiState == AiStates.ERROR) {
				setState({ aiState, error: ErrorStates.ISSUE_GENERATION });
			} else {
				setState({ aiState });
			}
		},
	setIsCreatedWithAi:
		(isCreatedWithAi: State['isCreatedWithAi']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedWithAi });
		},
	setIsCreatedFromTable:
		(isCreatedFromTable: State['isCreatedFromTable']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedFromTable });
		},
	setIsCreatedFromActionItems:
		(isCreatedFromActionItems: State['isCreatedFromActionItems']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedFromActionItems });
		},
	setContentLastModifiedTimestamp:
		(contentLastModifiedTimestamp: State['contentLastModifiedTimestamp']): Action<State> =>
		({ setState }) => {
			setState({ contentLastModifiedTimestamp });
		},
	setInsertStorageFragmentRequestPayload:
		(insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload): Action<State> =>
		({ setState }) => {
			setState({ insertStorageFragmentRequestPayload });
		},
	setInsertStorageColumnTableRequestPayload:
		(
			insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload,
		): Action<State> =>
		({ setState }) => {
			setState({ insertStorageColumnTableRequestPayload });
		},
	removeBulkIssue:
		(issueId: string): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList } = getState();
			const newBulkIssuesList = bulkIssuesList.filter((issue) => issue.id !== issueId);
			setState({
				bulkIssuesList: newBulkIssuesList,
				closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
			});
		},
	removeBulkIssueList:
		(issueIds: string[]): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList } = getState();
			setState({
				bulkIssuesList: bulkIssuesList.filter((issue) => !issueIds.includes(issue.id)),
			});
		},
	setSingleIssuesCreated:
		(singleIssue: CreateIssueResponse): Action<State> =>
		({ setState, getState }) => {
			const { singleIssuesCreated } = getState();
			setState({
				singleIssuesCreated: [...singleIssuesCreated, singleIssue],
			});
		},
	resetSingleIssuesCreated:
		(): Action<State> =>
		({ setState }) => {
			setState({
				singleIssuesCreated: initialBulkCreateState.singleIssuesCreated,
			});
		},
	setCurrentJiraSite:
		(currentJiraSite: State['currentJiraSite']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraSite });
		},
	setCurrentJiraProject:
		(currentJiraProject: State['currentJiraProject']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraProject });
		},
	setNextIssueToEdit:
		(goToPage: (page: AiBulkNavigationPageType) => void): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList, currentIssueId } = getState();
			const currentIssueListIndex = bulkIssuesList.findIndex(
				(issue) => issue?.id === currentIssueId,
			);
			const newBulkIssuesList = bulkIssuesList.filter((issue) => issue?.id !== currentIssueId);

			if (currentIssueListIndex >= bulkIssuesList.length - 1) {
				setState({
					bulkIssuesList: newBulkIssuesList,
					closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
				});
				void goToPage('default');
				return;
			}
			setState({
				currentIssueId: bulkIssuesList[currentIssueListIndex + 1]?.id,
				bulkIssuesList: newBulkIssuesList,
				closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
			});
		},
	setCurrentDefaultIssueType:
		(currentDefaultIssueType: State['currentDefaultIssueType']): Action<State> =>
		({ setState }) => {
			setState({ currentDefaultIssueType });
		},
	setIssueTypeHasRequiredFields:
		(issueTypeHasRequiredFields: State['issueTypeHasRequiredFields']): Action<State> =>
		({ setState }) => {
			setState({ issueTypeHasRequiredFields });
		},
	setError:
		(error: State['error']): Action<State> =>
		({ setState }) => {
			setState({ error });
		},
	setManualCreateState:
		(manualCreateState: State['manualCreateState']): Action<State> =>
		({ setState, getState }) => {
			const { error } = getState();
			const isManualError =
				error === ErrorStates.SITE_RETRIEVAL ||
				error === ErrorStates.PROJECT_RETRIEVAL ||
				error === ErrorStates.ISSUE_TYPE_RETRIEVAL;
			if (isManualError && manualCreateState === 'loading') {
				setState({ manualCreateState, error: ErrorStates.EMPTY });
			} else if (manualCreateState === 'error') {
				setState({ manualCreateState, error: ErrorStates.SITE_RETRIEVAL }); // Can be site , project or issue type retrieval since they all have the same error message
			} else {
				setState({ manualCreateState });
			}
		},
	// Resets transient bulk create state while preserving Jira configuration
	resetTransientBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState(initialTransientBulkCreateState);
		},
	resetManualBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState({ manualCreateState: 'idle', error: ErrorStates.EMPTY, bulkIssuesList: [] });
		},
	resetAIBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState({ bulkIssuesList: [], aiResultViewedFiredOnce: false });
		},
	setAiResultViewedFiredOnce:
		(aiResultViewedFiredOnce: State['aiResultViewedFiredOnce']): Action<State> =>
		({ setState }) => {
			setState({ aiResultViewedFiredOnce });
		},
	resetCloseAndAddIssuesToPage:
		(): Action<State> =>
		({ setState }) => {
			setState({
				closeAndAddIssuesToPage: false,
			});
		},
	setExpectedIssueIndices:
		(expectedIssueIndices: State['expectedIssueIndices']): Action<State> =>
		({ setState }) => {
			setState({ expectedIssueIndices });
		},
	setSelectedTableAdf:
		(selectedTableAdf: State['selectedTableAdf']): Action<State> =>
		({ setState }) => {
			setState({ selectedTableAdf });
		},
};

const Store = createStore({
	initialState: {
		...initialBulkCreateState,
	},
	actions: bulkCreateActions,
	name: 'bulkCreateContext',
});

export const BulkCreateContainer = createContainer(Store);

export const useBulkCreateContext = createHook(Store);

const useActions = createActionsHook(Store);

export const useSetIssueTypeFields = () => {
	const { setIssueTypeFields } = useActions();
	return setIssueTypeFields;
};

export const useSetIsCreatedWithAi = () => {
	const { setIsCreatedWithAi } = useActions();
	return setIsCreatedWithAi;
};

export const useSetIsCreatedFromTable = () => {
	const { setIsCreatedFromTable } = useActions();
	return setIsCreatedFromTable;
};

export const useSetIsCreatedFromActionItems = () => {
	const { setIsCreatedFromActionItems } = useActions();
	return setIsCreatedFromActionItems;
};

export const useSetCurrentIssueId = () => {
	const { setCurrentIssueId } = useActions();
	return setCurrentIssueId;
};

export const useRemoveBulkIssue = () => {
	const { removeBulkIssue } = useActions();
	return removeBulkIssue;
};

export const useSetNextIssueToEdit = () => {
	const { setNextIssueToEdit } = useActions();
	const goToPage = useAiBulkNavigationGotoPage();
	return useCallback(() => void setNextIssueToEdit(goToPage), [goToPage, setNextIssueToEdit]);
};

export const useSetIssueTypeHasRequiredFields = () => {
	const { setIssueTypeHasRequiredFields } = useActions();
	return setIssueTypeHasRequiredFields;
};

export const useSetSingleIssuesCreated = () => {
	const { setSingleIssuesCreated } = useActions();
	return setSingleIssuesCreated;
};

export const useResetSingleIssuesCreated = () => {
	const { resetSingleIssuesCreated } = useActions();
	return resetSingleIssuesCreated;
};

export const useGetContentLastModifiedTimestamp = createStateHook(Store, {
	selector: (state: State): string | null | undefined => state.contentLastModifiedTimestamp,
});

export const useSetContentLastModifiedTimestamp = () => {
	const { setContentLastModifiedTimestamp } = useActions();
	return setContentLastModifiedTimestamp;
};

export const useSetInsertStorageFragmentRequestPayload = () => {
	const { setInsertStorageFragmentRequestPayload } = useActions();
	return setInsertStorageFragmentRequestPayload;
};

export const useGetIssueType = createStateHook(Store, {
	selector: (state: State): IssueType => ({
		iconUrl: transformStargateUrl(state.currentDefaultIssueType?.iconUrl || ''),
		name: state.currentDefaultIssueType?.name || '',
		id: state.currentDefaultIssueType?.id || '',
	}),
});

export const useGetIsMissingColumnMappingError = createStateHook(Store, {
	selector: (state: State): boolean => !state.isCreatedWithAi && state.bulkIssuesList.length === 0,
});

export const useSetError = () => {
	const { setError } = useActions();
	return setError;
};

export const useCloseAndAddIssuesToPage = createStateHook(Store, {
	selector: (state: State): boolean => state.closeAndAddIssuesToPage,
});

export const useResetCloseAndAddIssuesToPage = () => {
	const { resetCloseAndAddIssuesToPage } = useActions();
	return resetCloseAndAddIssuesToPage;
};

export const useSetBulkIssuesList = () => {
	const { setBulkIssuesList } = useActions();
	return setBulkIssuesList;
};

export const useSetExpectedIssueIndices = () => {
	const { setExpectedIssueIndices } = useActions();
	return setExpectedIssueIndices;
};

export const useSetSelectedTableAdf = () => {
	const { setSelectedTableAdf } = useActions();
	return setSelectedTableAdf;
};
export const InitiativeWorkItemTypeName = 'Initiative';
export const EpicWorkItemTypeName = 'Epic';
